import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TaskData } from '@/lib/api';

const BACKOFF_INITIAL_MS = 1000;
const BACKOFF_MAX_MS = 30_000;
const BACKOFF_JITTER = 0.2; // ±20%
// How long the connection must be down before reporting disconnected to consumers.
// Prevents flashing "Reconnecting…" on initial page load or brief transient drops.
const DISCONNECT_GRACE_MS = 4_000;

function jittered(ms: number): number {
  const spread = ms * BACKOFF_JITTER;
  return ms + (Math.random() * 2 - 1) * spread;
}

/**
 * Global SSE hook — connects to /api/v1/tasks/stream and keeps TanStack Query
 * caches fresh.  Mount this once in AppLayout.
 *
 * Returns `connected` — true when the EventSource is open, false when
 * disconnected/reconnecting.
 */
export function useTaskStream(
  onTasksChange: (tasks: Map<string, TaskData>) => void,
  onTaskComplete?: (task: TaskData) => void,
): { connected: boolean } {
  const qc = useQueryClient();
  const tasksRef = useRef<Map<string, TaskData>>(new Map());
  const esRef = useRef<EventSource | null>(null);
  const fadeTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffDelay = useRef<number>(BACKOFF_INITIAL_MS);
  // Start optimistically true — only flip false after the grace period expires.
  const [connected, setConnected] = useState(true);

  const scheduleFade = useCallback((taskId: string) => {
    if (fadeTimers.current.has(taskId)) return;
    const t = setTimeout(() => {
      tasksRef.current.delete(taskId);
      fadeTimers.current.delete(taskId);
      onTasksChange(new Map(tasksRef.current));
    }, 30_000);
    fadeTimers.current.set(taskId, t);
  }, [onTasksChange]);

  useEffect(() => {
    let unmounted = false;

    const connect = () => {
      if (unmounted) return;
      if (esRef.current) esRef.current.close();

      const es = new EventSource('/api/v1/tasks/stream');
      esRef.current = es;

      es.onopen = () => {
        if (unmounted) return;
        // Cancel any pending disconnect notification and mark connected
        if (disconnectTimer.current !== null) {
          clearTimeout(disconnectTimer.current);
          disconnectTimer.current = null;
        }
        backoffDelay.current = BACKOFF_INITIAL_MS;
        setConnected(true);
      };

      es.onmessage = (event) => {
        try {
          const data: TaskData & { keepalive?: boolean } = JSON.parse(event.data);
          if (data.keepalive) return;

          const taskId = data.task_id;
          if (!taskId) return;

          const prev = tasksRef.current.get(taskId);

          // Skip already-finished tasks that arrive in the initial snapshot
          if (!prev && !data.is_active) return;

          tasksRef.current.set(taskId, data);

          // Always invalidate the task query for this deck so background primer
          // updates (fired after the task is already COMPLETED) reach the view.
          // System tasks (task_type: 'system') carry no deck_id — guarded below.
          if (data.task_type !== 'system' && data.deck_id) {
            void qc.invalidateQueries({ queryKey: ['deckTask', data.deck_id] });
          }

          if (prev?.is_active && !data.is_active) {
            // Also invalidate the deck itself when the task finishes
            if (data.task_type !== 'system' && data.deck_id) {
              void qc.invalidateQueries({ queryKey: ['deck', data.deck_id] });
            }
            scheduleFade(taskId);
            onTaskComplete?.(data);
          }

          onTasksChange(new Map(tasksRef.current));
        } catch {
          // ignore parse errors
        }
      };

      es.onerror = () => {
        if (unmounted) return;
        es.close();
        // Delay reporting disconnected so brief drops don't flash the indicator
        if (disconnectTimer.current === null) {
          disconnectTimer.current = setTimeout(() => {
            disconnectTimer.current = null;
            if (!unmounted) setConnected(false);
          }, DISCONNECT_GRACE_MS);
        }
        esRef.current = null;

        if (import.meta.env.DEV) {
          console.warn(`[TaskStream] SSE disconnected, reconnecting in ${Math.round(backoffDelay.current)}ms…`);
        }

        // Schedule reconnect with current backoff delay
        reconnectTimer.current = setTimeout(() => {
          if (!unmounted) connect();
        }, jittered(backoffDelay.current));

        // Exponential backoff — double for next failure, capped at max
        backoffDelay.current = Math.min(backoffDelay.current * 2, BACKOFF_MAX_MS);
      };
    };

    connect();

    return () => {
      unmounted = true;
      esRef.current?.close();
      esRef.current = null;
      if (reconnectTimer.current !== null) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (disconnectTimer.current !== null) {
        clearTimeout(disconnectTimer.current);
        disconnectTimer.current = null;
      }
      for (const t of fadeTimers.current.values()) clearTimeout(t);
      fadeTimers.current.clear();
    };
  }, [qc, onTasksChange, onTaskComplete, scheduleFade]);

  return { connected };
}
